import useTerms from "../../../hooks/useTerms";
import React, { useEffect, useState } from "react";
import { Box, Typography, alpha } from "@mui/material";
import { Inventory2Outlined as PackageIcon, AddRounded as AddIcon } from "@mui/icons-material";
import { DrawerField } from "../../molecules";
import { EmptyState, FormDatePicker } from "../../atoms";
import { useClinicTheme } from "../../../theme";
import { COLORS, TYPO } from "../../../styles/uiConstants";
import { formatMoney } from "../../../utils/money";
import { useListPackageTemplatesMutation } from "../../../store/billingApi";
import { DrawerInput, FormDrawer, todayYmd } from "./billingParts";

/**
 * Hastaya paket satışı: şablon seç → başlangıç tarihi → not. Satış hastanın
 * hesabına paket fiyatı kadar borç yazar; ödeme ayrıca "Ödeme al" ile girilir.
 */
export default function SellPackageDrawer({ open, onClose, token, clinicId, onSell, saving }) {
  const terms = useTerms();
  const { palette } = useClinicTheme();
  const [templates, setTemplates] = useState(null);
  const [templateId, setTemplateId] = useState("");
  const [startedAt, setStartedAt] = useState(todayYmd());
  const [note, setNote] = useState("");
  const [listTemplates] = useListPackageTemplatesMutation();

  useEffect(() => {
    if (!open || !token) return;
    setTemplateId("");
    setStartedAt(todayYmd());
    setNote("");
    setTemplates(null);
    listTemplates({ token, ...(clinicId ? { clinicId } : {}) })
      .unwrap()
      .then((r) => setTemplates(r?.data?.templates || []))
      .catch(() => setTemplates([]));
  }, [open, token, clinicId, listTemplates]);

  const selected = (templates || []).find((t) => t._id === templateId);
  const submit = () =>
    selected &&
    onSell({
      templateId,
      startedAt: startedAt ? new Date(`${startedAt}T12:00:00`).toISOString() : undefined,
      note: note.trim() || undefined,
    });

  return (
    <FormDrawer
      open={open}
      onClose={onClose}
      icon={<PackageIcon />}
      title="Paket sat"
      subtitle={`Paket fiyatı ${terms.patientGenLower} hesabına borç olarak yazılır.`}
      width={520}
      onSubmit={submit}
      submitLabel={selected ? `Sat · ${formatMoney(selected.price)}` : "Sat"}
      startIcon={<AddIcon />}
      loading={saving}
      disabled={!selected}
    >
      {templates && templates.length === 0 ? (
        <EmptyState
          variant="illustrated-sm"
          icon={<PackageIcon />}
          title="Satışta paket yok"
          description="Paketler Klinik Yönetimi › Hizmetler ve Paketler sayfasında tanımlanır."
        />
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          <DrawerField label="Paket *">
            <Box role="radiogroup" aria-label="Paket" sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 0.75 }}>
              {(templates || []).map((t) => {
                const active = t._id === templateId;
                return (
                  <Box
                    key={t._id}
                    role="radio"
                    aria-checked={active}
                    tabIndex={0}
                    onClick={() => setTemplateId(t._id)}
                    onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setTemplateId(t._id)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      px: 1.75,
                      py: 1.25,
                      borderRadius: 2,
                      cursor: "pointer",
                      border: `1px solid ${active ? palette.primary : COLORS.slate200}`,
                      bgcolor: active ? alpha(palette.primary, 0.06) : COLORS.white,
                      "&:focus-visible": { outline: `2px solid ${alpha(palette.primary, 0.4)}` },
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography noWrap sx={{ fontSize: TYPO.body, fontWeight: 600, color: COLORS.slate800 }}>
                        {t.name}
                      </Typography>
                      <Typography noWrap sx={{ fontSize: TYPO.caption, color: COLORS.slate500 }}>
                        {[t.service?.name, `${t.sessionCount} seans`, `${t.validityDays} gün geçerli`]
                          .filter(Boolean)
                          .join(" · ")}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: TYPO.body, fontWeight: 600, color: COLORS.slate800, fontVariantNumeric: "tabular-nums" }}>
                      {formatMoney(t.price)}
                    </Typography>
                  </Box>
                );
              })}
              {templates === null
                ? [0, 1].map((i) => (
                    <Box key={i} sx={{ height: 52, borderRadius: 2, bgcolor: COLORS.slate100 }} />
                  ))
                : null}
            </Box>
          </DrawerField>
          <DrawerField
            label="Başlangıç tarihi"
            hint={selected ? `Paket ${selected.validityDays} gün sonra, gün sonunda sona erer.` : undefined}
          >
            <FormDatePicker
              variant="standard"
              palette={palette}
              fullWidth
              value={startedAt}
              onChange={setStartedAt}
            />
          </DrawerField>
          <DrawerField label="Not">
            <DrawerInput
              palette={palette}
              multiline
              minRows={2}
              placeholder="İsteğe bağlı"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              inputProps={{ maxLength: 500, "aria-label": "Not" }}
            />
          </DrawerField>
        </Box>
      )}
    </FormDrawer>
  );
}
